import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SUBJECTS = [
  { nom: "Français", coefficient: 4 },
  { nom: "Mathématiques", coefficient: 4 },
  { nom: "Éveil", coefficient: 2 },
  { nom: "Histoire-Géographie", coefficient: 2 },
  { nom: "Éducation physique", coefficient: 1 },
];

export default function Subjects() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: "", coefficient: 1, mode: "simple", sous_matieres: [] });

  useEffect(() => {
    api.get("/classes").then(({ data }) => {
      setClasses(data);
      if (data.length > 0) setClassId(data[0].id);
    });
  }, []);

  const load = async () => {
    if (!classId) return;
    try {
      const { data } = await api.get("/subjects", { params: { class_id: classId } });
      setSubjects(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [classId]);

  const create = async () => {
    try {
      await api.post("/subjects", { ...form, class_id: classId });
      toast.success("Matière créée");
      setOpen(false);
      setForm({ nom: "", coefficient: 1, mode: "simple", sous_matieres: [] });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (s) => {
    if (!confirm(`Supprimer ${s.nom} ? (les notes seront perdues)`)) return;
    try {
      await api.delete(`/subjects/${s.id}`);
      toast.success("Supprimée");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const seed = async () => {
    if (!confirm("Ajouter les matières standard ?")) return;
    for (const m of DEFAULT_SUBJECTS) {
      try { await api.post("/subjects", { ...m, mode: "simple", sous_matieres: [], class_id: classId }); }
      catch { /* ignore */ }
    }
    toast.success("Matières standard ajoutées");
    load();
  };

  const addSub = () => setForm({ ...form, sous_matieres: [...(form.sous_matieres || []), { nom: "", coefficient: 1 }] });
  const updSub = (i, k, v) => {
    const arr = [...form.sous_matieres]; arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, sous_matieres: arr });
  };
  const rmSub = (i) => setForm({ ...form, sous_matieres: form.sous_matieres.filter((_, idx) => idx !== i) });

  const canEdit = user.role === "maitre";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Matières</h1>
          <p className="text-sm text-[#5C736A]">Coefficients et sous-matières (facultatif)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-44" data-testid="subj-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && subjects.length === 0 && (
            <Button variant="outline" onClick={seed} data-testid="seed-subjects">Ajouter standard</Button>
          )}
          {canEdit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="add-subject-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nouvelle matière
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nouvelle matière</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Nom</Label><Input data-testid="subj-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
                  <div><Label>Coefficient</Label><Input type="number" min="0.5" step="0.5" value={form.coefficient} onChange={(e) => setForm({ ...form, coefficient: Number(e.target.value) })} /></div>
                  <div>
                    <Label>Mode</Label>
                    <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simple">Note unique</SelectItem>
                        <SelectItem value="sous_matieres">Sous-matières</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.mode === "sous_matieres" && (
                    <div className="space-y-2">
                      <Label>Sous-matières</Label>
                      {form.sous_matieres.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Input placeholder="Nom" value={s.nom} onChange={(e) => updSub(i, "nom", e.target.value)} />
                          <Input type="number" step="0.5" placeholder="Coef" value={s.coefficient} onChange={(e) => updSub(i, "coefficient", Number(e.target.value))} className="w-24" />
                          <Button variant="ghost" size="icon" onClick={() => rmSub(i)}><X className="w-4 h-4" /></Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={addSub}><Plus className="w-3 h-3 mr-1" /> Ajouter</Button>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button onClick={create} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="subj-submit">Créer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="ek-card p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Matière</TableHead>
              <TableHead>Coefficient</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Sous-matières</TableHead>
              {canEdit && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {subjects.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-semibold">{s.nom}</TableCell>
                <TableCell>{s.coefficient}</TableCell>
                <TableCell>{s.mode === "sous_matieres" ? "Sous-matières" : "Note unique"}</TableCell>
                <TableCell className="text-sm text-[#5C736A]">
                  {(s.sous_matieres || []).map((x) => x.nom).join(", ") || "—"}
                </TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => del(s)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {subjects.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-[#5C736A] py-8">Aucune matière. Cliquez sur « Ajouter standard » ou créez-en une.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
